import { status as AppointmentStatusIds } from '@spike/appointment-model';
import { WorkflowStepAction } from '@spike/masterdata-model';
import clsx from 'clsx';
import { FunctionComponent, RefObject, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import styles from './BulkStatusMenu.module.scss';

const actionStatusIds = [AppointmentStatusIds.CANCELLED, AppointmentStatusIds.NO_SHOW, AppointmentStatusIds.DECLINED];
const desktopRedStatusIds = [AppointmentStatusIds.DECLINED];
const mobileRedStatusIds = [
    AppointmentStatusIds.CANCELLED,
    AppointmentStatusIds.NO_SHOW,
    AppointmentStatusIds.DECLINED
];

const getLabel = (action: WorkflowStepAction) =>
    actionStatusIds.includes(action.nextStatus.id) ? action.action : action.nextStatus.name;

interface BulkStatusMenuProps {
    actions: Array<WorkflowStepAction>;
    onClick: (statusId: number, statusName: string) => void;
    onClose: () => void;
    /** Clicks inside this element should not count as "outside" (e.g. the black pill that toggles the menu). */
    ignoreRef?: RefObject<HTMLElement>;
}

export const BulkStatusMenu: FunctionComponent<BulkStatusMenuProps> = props => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const redStatusIds = isMobile ? mobileRedStatusIds : desktopRedStatusIds;

    useEffect(() => {
        if (isMobile) {
            return;
        }

        // Use `click` (not `mousedown`/`touchstart`) so this fires *after* the toggle button's
        // own click handler. Otherwise, clicking the icon to close an already-open menu would
        // first close it here (mousedown, before the click), then the icon's click handler would
        // toggle it back open right away, making it look like clicking the icon never closes it.
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (containerRef.current?.contains(target)) {
                return;
            }
            if (props.ignoreRef?.current?.contains(target)) {
                return;
            }
            props.onClose();
        };

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile]);

    useEffect(() => {
        if (!isMobile) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isMobile]);

    const menuContent = (
        <>
            <span className={styles.title}>Change Status</span>
            {props.actions.map(action => (
                <div
                    key={action.nextStatus.id}
                    className={styles.item}
                    onClick={() => props.onClick(action.nextStatus.id, action.nextStatus.name)}
                >
                    <span
                        className={redStatusIds.includes(action.nextStatus.id) ? styles.itemLabelRed : styles.itemLabel}
                    >
                        {getLabel(action)}
                    </span>
                </div>
            ))}
        </>
    );

    if (isMobile) {
        return createPortal(
            <div
                className={styles.overlay}
                onClick={props.onClose}
            >
                <div
                    ref={containerRef}
                    className={clsx(styles.container, styles.containerMobile)}
                    onClick={event => event.stopPropagation()}
                >
                    <div className={styles.handle} />
                    {menuContent}
                </div>
            </div>,
            document.body
        );
    }

    return (
        <div
            ref={containerRef}
            className={styles.container}
        >
            {menuContent}
        </div>
    );
};

export default BulkStatusMenu;
